import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { canAccess, MODULES } from '../../../auth/permissions';
import { logError } from '../../../utils/logger';
import { useAuth } from '../../auth/auth.context';
import { SectionTitle } from '../../shared/components';
import { Table } from '../../shared/components/data-display';
import useAsyncData from '../../shared/hooks/useAsyncData';
import JobStepDialog from './JobStepDialog';
import WorkJobActionsModal from './WorkJobActionsModal';
import { useQuotationRights } from '../../../auth/access';
import WorkJobEditModal from './WorkJobEditModal';
import {
  downloadWorkJobReportExcel,
  EMPTY_FILTER_OPTIONS,
  getWorkJobReport,
  getWorkJobReportFilterOptions,
  WorkJobDropdownFilterKey,
  WorkJobReportFilterOptions,
  WorkJobReportFilters,
  WorkJobReportRow,
} from './workJobReport.api';

/** How long typing settles before the search re-runs the report, in ms. */
const SEARCH_DEBOUNCE_MS = 350;

/** The dropdowns, in the order they read on screen: what the job is, then who it is for. */
const FILTER_FIELDS: Array<{
  key: WorkJobDropdownFilterKey;
  label: string;
  optionKey: keyof WorkJobReportFilterOptions;
}> = [
  { key: 'project_type_id', label: 'Project Type', optionKey: 'project_types' },
  { key: 'construction_work_type_id', label: 'Construction Work Type', optionKey: 'construction_work_types' },
  { key: 'building_type_id', label: 'Building Type', optionKey: 'building_types' },
  { key: 'status_id', label: 'Status', optionKey: 'statuses' },
  { key: 'financial_year_id', label: 'Financial Year', optionKey: 'financial_years' },
  { key: 'company_id', label: 'Company', optionKey: 'companies' },
  { key: 'client_type', label: 'Client', optionKey: 'client_types' },
  { key: 'architect_id', label: 'Architect', optionKey: 'architects' },
  { key: 'builder_id', label: 'Builder', optionKey: 'builders' },
  { key: 'owner_id', label: 'Owner', optionKey: 'owners' },
];

const NO_VALUE = <span className='unavailable-message'>—</span>;

/** Job dates arrive as ISO `yyyy-mm-dd`; the app shows dates the Indian way. */
const formatDate = (value: string | null) => {
  if (!value) return NO_VALUE;
  const [year, month, day] = value.split('-');
  return day && month && year ? `${day}-${month}-${year}` : value;
};

const formatAmount = (value: number | null) =>
  value == null ? NO_VALUE : `₹ ${Number(value).toLocaleString('en-IN')}`;

const formatNumber = (value: number | null) =>
  value == null ? NO_VALUE : Number(value).toLocaleString('en-IN');

const baseColumns = [
  { id: 'job_code', dataKey: 'job_code', label: 'Job Code', align: 'left', sortable: true },
  { id: 'job_name', dataKey: 'job_name', label: 'Job Name', align: 'left', sortable: true },
  {
    id: 'job_date',
    dataKey: 'job_date',
    label: 'Job Date',
    align: 'center',
    sortable: true,
    renderCell: formatDate,
  },
  { id: 'financial_year', dataKey: 'financial_year', label: 'Financial Year', align: 'center', sortable: true },
  { id: 'project_type', dataKey: 'project_type', label: 'Project Type', align: 'left', sortable: true },
  { id: 'construction_work_type', dataKey: 'construction_work_type', label: 'Construction Work Type', align: 'left', sortable: true },
  { id: 'building_type', dataKey: 'building_type', label: 'Building Type', align: 'left', sortable: true },
  { id: 'status', dataKey: 'status', label: 'Status', align: 'center', sortable: true },
  { id: 'client_name', dataKey: 'client_name', label: 'Client', align: 'left', sortable: true },
  { id: 'architect_name', dataKey: 'architect_name', label: 'Architect', align: 'left', sortable: true },
  { id: 'builder_name', dataKey: 'builder_name', label: 'Builder', align: 'left', sortable: true },
  { id: 'owner_name', dataKey: 'owner_name', label: 'Owner', align: 'left', sortable: true },
  { id: 'locality', dataKey: 'locality', label: 'Locality', align: 'left', sortable: true },
  {
    id: 'total_after_discount',
    dataKey: 'total_after_discount',
    label: 'Job Value',
    align: 'right',
    sortable: true,
    renderCell: formatAmount,
  },
  {
    id: 'milestones',
    dataKey: 'milestone_count',
    label: 'Milestones',
    align: 'center',
    renderCell: (value: number | null, row: WorkJobReportRow) =>
      value == null ? NO_VALUE : `${formatNumber(row.milestones_completed ?? 0)}/${formatNumber(value)}`,
  },
];

/**
 * Work-job list report: a live search box and the work_jobs foreign keys as
 * filters, the matching rows in a table, and an Excel export of exactly that
 * selection. The export re-runs the same query server-side, so it carries every
 * column — including the site address and cost breakdown the table leaves out for
 * width.
 *
 * Search is a server-side filter, not a pass over the loaded rows: it matches every
 * text column the report holds (site address included, which the table never shows)
 * and it reaches the export, so the workbook is always the table on screen.
 */
export default function WorkJobReportPage() {
  const { role } = useAuth();
  const [filters, setFilters] = useState<WorkJobReportFilters>({});
  // The box is uncontrolled by `filters` so typing stays instant; `filters.q` catches
  // up once the user pauses, and only that triggers a request.
  const [searchInput, setSearchInput] = useState('');
  const [exporting, setExporting] = useState(false);
  const [editingRow, setEditingRow] = useState<WorkJobReportRow | null>(null);
  /** The row whose Job Actions dialog is open, if any. */
  const [actionsRow, setActionsRow] = useState<WorkJobReportRow | null>(null);

  useEffect(() => {
    const term = searchInput.trim();
    // Nothing to settle when the box already agrees with the running query — this
    // also keeps `clearFilters` from scheduling a redundant re-fetch.
    if (term === (filters.q ?? '')) return;
    const timer = setTimeout(
      () => setFilters((prev) => ({ ...prev, q: term })),
      SEARCH_DEBOUNCE_MS
    );
    return () => clearTimeout(timer);
  }, [searchInput, filters.q]);

  const canEdit = canAccess(role, MODULES.WORK_JOB_EDIT);
  /** V36 — Accounts raises quotations without editing jobs, so it gets the Quotation panel alone. */
  const { canRaise: canQuote } = useQuotationRights();
  const [quotationRow, setQuotationRow] = useState<WorkJobReportRow | null>(null);

  const activeFilterCount = useMemo(
    () => Object.values(filters).filter((value) => value !== undefined && value !== '').length,
    [filters]
  );

  /** No filters means no report: the page opens empty and waits for a selection. */
  const hasFilters = activeFilterCount > 0;

  const fetchOptions = useCallback(() => getWorkJobReportFilterOptions(), []);
  const {
    data: options,
    error: optionsError,
    refetch: refetchOptions,
  } = useAsyncData<WorkJobReportFilterOptions>(fetchOptions, EMPTY_FILTER_OPTIONS, {
    code: 'WORK_JOB_REPORT_FILTERS_FAILED',
    errorMessage: 'Failed to load report filters',
    toastOnError: false,
  });

  /** Every list arrives in one response, so any one of them proves the fetch landed. */
  const optionsLoaded = options.project_types.length > 0;

  // This single fetch feeds both the filter dropdowns here and every dropdown in the
  // edit dialog, and nothing retried it: one failure on page load — an expired token
  // is the usual one — left the dialog's lookups empty for the rest of the session,
  // which reads as "the search box stopped working". Retry when the dialog opens.
  useEffect(() => {
    if (editingRow && !optionsLoaded) refetchOptions();
  }, [editingRow, optionsLoaded, refetchOptions]);

  const fetchRows = useCallback(() => getWorkJobReport(filters), [filters]);
  const {
    data: rows,
    loading,
    error,
    refetch,
    setData: setRows,
  } = useAsyncData<WorkJobReportRow[]>(fetchRows, [], {
    enabled: hasFilters,
    code: 'WORK_JOB_REPORT_FAILED',
    errorMessage: 'Failed to load work job report',
  });

  // `enabled: false` only skips the request — it leaves the last result in state, so
  // clearing every filter would otherwise keep the previous report on screen.
  useEffect(() => {
    if (!hasFilters) setRows([]);
  }, [hasFilters, setRows]);

  /**
   * The edit affordance is a column, so it only exists for the roles that may use it.
   * It leads the row rather than trailing it: the report is wide enough to scroll
   * sideways, and a trailing Actions column sits off-screen until the reader scrolls
   * past every other column to reach it.
   *
   * Two buttons: the pencil edits the job's own fields, and the second opens everything
   * else that can be done to the job — Work Details, Quotation and Assign Work.
   */
  const columns = useMemo(() => {
    if (!canEdit) {
      if (!canQuote) return baseColumns;
      return [
        {
          id: 'actions',
          dataKey: 'id',
          label: 'Actions',
          align: 'center' as const,
          renderCell: (_value: unknown, row: WorkJobReportRow) => (
            <Tooltip title='Quotation'>
              <IconButton size='small' onClick={() => setQuotationRow(row)} aria-label='Quotation'>
                <Icon icon='mdi:file-document-outline' width={18} />
              </IconButton>
            </Tooltip>
          ),
        },
        ...baseColumns,
      ];
    }
    return [
      {
        id: 'actions',
        dataKey: 'id',
        label: 'Actions',
        align: 'center' as const,
        renderCell: (_value: unknown, row: WorkJobReportRow) => (
          <Stack direction='row' gap={0.5} justifyContent='center'>
            <Tooltip title='Edit job'>
              <IconButton size='small' onClick={() => setEditingRow(row)} aria-label='Edit job'>
                <Icon icon='mdi:pencil-outline' width={18} />
              </IconButton>
            </Tooltip>
            <Tooltip title='Job actions — work details, quotation, assign work'>
              <IconButton
                size='small'
                onClick={() => setActionsRow(row)}
                aria-label='Job actions'
              >
                <Icon icon='mdi:dots-horizontal-circle-outline' width={18} />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
      ...baseColumns,
    ];
  }, [canEdit, canQuote]);

  const setFilter = (key: WorkJobDropdownFilterKey, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const clearFilters = () => {
    setSearchInput('');
    setFilters({});
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await downloadWorkJobReportExcel(filters);
      toast.success('Report exported');
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to export work job report',
          error: err,
          code: 'WORK_JOB_REPORT_EXPORT_FAILED',
        })
      );
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack gap={2}>
      <SectionTitle
        title='WORK JOB REPORT'
        meta={
          <Typography variant='body2' color='text.secondary' whiteSpace='nowrap'>
            {!hasFilters
              ? 'Search or select a filter to run the report'
              : loading
                ? 'Loading…'
                : `${rows.length} job${rows.length === 1 ? '' : 's'} · ${activeFilterCount} filter${activeFilterCount === 1 ? '' : 's'}`}
          </Typography>
        }
        action={
          <Button
            variant='contained'
            onClick={handleExport}
            disabled={exporting || loading || rows.length === 0}
            startIcon={<Icon icon='mdi:microsoft-excel' />}
          >
            {exporting ? 'Exporting…' : 'Export to Excel'}
          </Button>
        }
      />

      {optionsError && (
        <Alert severity='warning'>
          Filter options could not be loaded, so the dropdowns are empty. {optionsError}
        </Alert>
      )}
      {error && <Alert severity='error'>{error}</Alert>}

      {/*
        One box over the whole row: it searches job code, name, locality, site address
        and every lookup label at once, so a user who knows any one of them need not
        work out which dropdown it lives behind.
      */}
      <TextField
        fullWidth
        size='small'
        label='Search'
        placeholder='Search job code, name, locality, address, client, architect, builder, owner…'
        value={searchInput}
        onChange={(event) => setSearchInput(event.target.value)}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <Icon icon='mdi:magnify' width={20} />
              </InputAdornment>
            ),
            endAdornment: searchInput ? (
              <InputAdornment position='end'>
                <IconButton size='small' onClick={() => setSearchInput('')} aria-label='Clear search'>
                  <Icon icon='mdi:close' width={16} />
                </IconButton>
              </InputAdornment>
            ) : null,
          },
        }}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
          gap: 2,
          alignItems: 'end',
        }}
      >
        {FILTER_FIELDS.map(({ key, label, optionKey }) => {
          // Ids are numbers, except the client filter's BUILDER / ARCHITECT / OWNER (V36).
          const list = options[optionKey] as Array<{ id: number | string; label: string }>;
          return (
            <Autocomplete
              key={key}
              options={list}
              value={list.find((option) => String(option.id) === filters[key]) ?? null}
              onChange={(_event, option) => setFilter(key, option ? String(option.id) : '')}
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              size='small'
              openOnFocus
              autoHighlight
              // Clearing the box means "All", which is exactly an empty filter value.
              renderInput={(params) => <TextField {...params} label={label} placeholder='All' />}
            />
          );
        })}

        <Button
          variant='outlined'
          onClick={clearFilters}
          disabled={activeFilterCount === 0}
          sx={{ height: 40 }}
        >
          Clear Filters
        </Button>
      </Box>

      <Table
        columns={columns}
        data={rows}
        loading={loading}
        emptyMessage={
          hasFilters
            ? 'No jobs match this search'
            : 'Search or choose one or more filters above to generate the report.'
        }
        resetSortKey={JSON.stringify(filters)}
      />

      <WorkJobEditModal
        row={editingRow}
        onMastersChanged={refetchOptions}
        options={options}
        onClose={() => setEditingRow(null)}
        onSaved={refetch}
      />

      <JobStepDialog
        step={quotationRow ? 'quotation' : null}
        row={quotationRow}
        onClose={() => setQuotationRow(null)}
        onSaved={refetch}
      />
      <WorkJobActionsModal
        row={actionsRow}
        onClose={() => setActionsRow(null)}
        onSaved={refetch}
      />
    </Stack>
  );
}
